import { ArrayElement } from "../../react-helpers/type";
import { Profile } from "../profile/profileModel";
import { Subscription } from "../subscription/subscriptionModel";
import { User } from "../user/userModel";

export interface Organization {
  id: number;
  legalName: string;
  slug: string;
  subscriptionStartDate: string | null;
  subscriptionEndDate: string | null;
  /** Null = pending manual validation by an admin (public email domain) */
  validationDate?: string | null;
  maxCapsulesCount: number;
  maxSponsorshipsCount: number;
  maxMembersCount: number;
  phoneNumber: string | null;
  email: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  subscriptions?: Subscription[];
  sharedProfiles: {
    createdAt: string;
    archivedAt: string | null;
    favoritedAt: string | null;
    profileId: Profile["id"];
    profile: Profile;
    /** Free-plan quota: out of the user's 3 most recent shares */
    locked?: boolean;
  }[];
  stripeCustomer?: {
    name: string;
    card?: {
      last4: string;
      brand: string;
      display_brand: string;
      exp_year: number;
      exp_month: number;
    };
    sepa?: {
      bank_code: string;
      branch_code: string;
      country: string;
      last4: string;
    };
  };
  users?: User[];
  invitations?: Invitation[];
}

/**
 * Mirror of the backend viewing gates (`GET /profiles/uid/:uid` and the
 * `locked` flags of `organizationWithSharedProfiles`): any still-valid
 * invitation grants unlimited visibility on shared profiles, even when the
 * inviting organization has no active subscription. A member disabled by an
 * org admin loses the invitation that made them join their own organization,
 * but keeps GUEST invitations from other organizations.
 */
export function hasUnlimitedSharedProfilesVisibility(
  loggedUser?: User | null,
): boolean {
  const isDisabledMember =
    loggedUser?.organizationMembershipStatus === "disabled";
  return (loggedUser?.invitations ?? []).some(
    (invitation) =>
      !isDisabledMember ||
      invitation.organizationId !== loggedUser?.organizationId,
  );
}

/**
 * Free pro account: no active subscription on the organization and no valid
 * invitation. Mirrors the backend viewing gate and the dashboard banner.
 */
export function isFreeOrganizationAccount(
  organization: Organization,
  loggedUser?: User | null,
): boolean {
  // Mirror of the backend viewing gates: a member disabled by an org admin
  // loses the invitation that made them join their own organization, but
  // keeps GUEST invitations from other organizations.
  const isDisabledMember =
    loggedUser?.organizationMembershipStatus === "disabled";
  const invitation =
    loggedUser?.invitations?.find(
      (i) =>
        !isDisabledMember || i.organizationId !== loggedUser.organizationId,
    ) ?? null;
  const hasValidInvitation =
    (invitation?.organization?.subscriptions?.length ?? 0) > 0;
  return !hasValidInvitation && (organization.subscriptions ?? []).length === 0;
}

export type NewOrganizationData = {
  [k in keyof Pick<
    Organization,
    | "legalName"
    | "phoneNumber"
    | "email"
    | "addressLine1"
    | "addressLine2"
    | "postalCode"
    | "city"
    | "country"
  >]: NonNullable<Organization[k]>;
};

export interface Invitation {
  id: number;
  uid: string;
  email: string;
  firstname: string;
  lastname: string;
  membershipType: ArrayElement<typeof INVITATION_MEMBERSHIP_TYPES>;
  disabledDate?: string | null;
  organizationId: Organization["id"];
  organization?: Organization;
  userId?: User["id"] | null;
  user?: User | null;
}

export const INVITATION_MEMBERSHIP_TYPES = [
  "ADMIN",
  "MEMBER",
  "GUEST",
] as const;

export interface CandidateInvitation {
  id: number;
  email: string;
  deadline: string | null;
  createdAt: string;
  organizationId: Organization["id"];
}
